import React, { useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FaSolarPanel,
  FaBolt,
  FaArrowRight,
  FaFacebook,
  FaInstagram,
  FaTicketAlt,
  FaUtensils,
  FaMapMarkerAlt,
  FaTrophy,
} from "react-icons/fa";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const WARM_AMBER = "#F2B705";
const BURNT_BRONZE = "#B87333";
const NAVY = "#141C49";

const chargeFill = keyframes`
  0%   { height: 0% }
  60%  { height: 100% }
  78%  { height: 100% }
  100% { height: 0% }
`;

const boltFlash = keyframes`
  0%, 52%  { opacity: 1; }
  62%       { opacity: 0.08; }
  72%       { opacity: 1; }
  82%       { opacity: 0.08; }
  92%, 100% { opacity: 1; }
`;

const panelPulse = keyframes`
  0%, 100% { opacity: 0.22; }
  50%       { opacity: 0.6; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 5px 2px 22px rgba(20,28,73,0.52), 0 0 12px rgba(198,146,20,0.2); }
  50%       { box-shadow: 5px 2px 32px rgba(20,28,73,0.7), 0 0 24px rgba(198,146,20,0.58); }
`;

const PRIZE_TILES = [
  {
    icon: FaTicketAlt,
    label: "4 Premium Tickets",
    detail: "Bring three guests for a full game day experience.",
    accent: SOLAR_GOLD,
  },
  {
    icon: FaMapMarkerAlt,
    label: "Half Way Line",
    detail: "Outdoor corporate box — right where the action is.",
    accent: WARM_AMBER,
  },
  {
    icon: FaUtensils,
    label: "Food & Drinks",
    detail: "All covered inside your corporate box.",
    accent: BURNT_BRONZE,
  },
  {
    icon: FaTrophy,
    label: "One Game. Unforgettable.",
    detail: "Brisbane Broncos home game — June 2026.",
    accent: SOLAR_GOLD,
  },
];

const SolarGiveawayJune: React.FC = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://webforms.pipedrive.com/f/loader";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/giveaway-june"
        fallbackTitle="Win 4 Broncos Tickets | One Solar Australia June Giveaway"
        fallbackDescription="Purchase a solar and battery package this month and go in the draw to WIN 4 premium tickets to a Brisbane Broncos game day experience — including food and drinks in our outdoor corporate box."
      />
      <NavbarWithCallToAction />

      {/* ── Floating Quote Button ── */}
      <Box
        as={RouterLink}
        to="/solar/quote"
        position="fixed"
        left="14px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={100}
        w="62px"
        h="74px"
        bg={NAVY}
        borderRadius="14px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="5px"
        cursor="pointer"
        overflow="hidden"
        color="white"
        _hover={{ bg: SOLAR_GOLD, transform: "translateY(-50%) scale(1.08)" }}
        transition="background-color 0.22s, transform 0.22s"
        sx={{ animation: `${glowPulse} 2.6s ease-in-out infinite` }}
      >
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
          sx={{ animation: `${panelPulse} 2.6s ease-in-out infinite` }}
        >
          <Icon as={FaSolarPanel} boxSize={14} color={SOLAR_GOLD} />
        </Box>
        <Text fontSize="8px" fontWeight="800" letterSpacing="0.12em" textTransform="uppercase" color="white" lineHeight={1} position="relative" zIndex={2}>Get</Text>
        <Box position="relative" w="30px" h="28px" zIndex={2} flexShrink={0}>
          <svg width="30" height="28" viewBox="0 0 30 28" fill="none" style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}>
            <rect x="1" y="4" width="23" height="20" rx="3" stroke="white" strokeWidth="2.4" />
            <rect x="24.2" y="9" width="4.8" height="10" rx="2" fill="white" />
          </svg>
          <Box position="absolute" left="4px" top="7px" w="17px" h="14px" overflow="hidden" borderRadius="1.5px" zIndex={2}>
            <Box position="absolute" bottom={0} left={0} right={0} bg={SOLAR_GOLD} borderRadius="1.5px" sx={{ animation: `${chargeFill} 2.6s ease-in-out infinite` }} />
          </Box>
          <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" zIndex={3} sx={{ animation: `${boltFlash} 2.6s ease-in-out infinite` }}>
            <Icon as={FaBolt} color="white" w="10px" h="10px" />
          </Box>
        </Box>
        <Text fontSize="8px" fontWeight="800" letterSpacing="0.12em" textTransform="uppercase" color="white" lineHeight={1} position="relative" zIndex={2}>Quote</Text>
      </Box>

      {/* ── Social sidebar ── */}
      <VStack
        position="fixed"
        left="14px"
        bottom="110px"
        spacing={3}
        zIndex={100}
      >
        <Box
          as="a"
          href="https://www.facebook.com/One-Solar-100522258324560"
          target="_blank"
          rel="noopener noreferrer"
          w={9} h={9}
          borderRadius="full"
          bg={NAVY}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          _hover={{ bg: SOLAR_GOLD }}
          transition="all 0.2s"
        >
          <FaFacebook size={15} />
        </Box>
        <Box
          as="a"
          href="https://www.instagram.com/onesolar.australia.official"
          target="_blank"
          rel="noopener noreferrer"
          w={9} h={9}
          borderRadius="full"
          bg={NAVY}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          _hover={{ bg: SOLAR_GOLD }}
          transition="all 0.2s"
        >
          <FaInstagram size={15} />
        </Box>
      </VStack>

      {/* ── HERO — WIN 4 BRONCOS TICKETS ── */}
      <Box
        position="relative"
        minH={{ base: "85vh", md: "90vh" }}
        display="flex"
        alignItems="center"
        overflow="hidden"
        backgroundImage="url('/assets/solar-assets/FB-Ad-Photo-grid-Facebook-Cover-2-e1780490951861.jpg')"
        backgroundSize="cover"
        backgroundPosition={{ base: "center", md: "right center" }}
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
      >
        {/* White-to-transparent gradient fade from left */}
        <Box
          position="absolute"
          inset={0}
          zIndex={0}
          bg="linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.96) 28%, rgba(255,255,255,0.75) 48%, rgba(255,255,255,0) 70%)"
        />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <Box maxW={{ base: "full", md: "52%" }}>
            <VStack spacing={6} textAlign={{ base: "center", md: "left" }} align={{ base: "center", md: "flex-start" }}>
              {/* Big WIN headline */}
              <VStack spacing={0} lineHeight="0.9">
                <Text
                  fontSize={{ base: "3xl", md: "5xl" }}
                  fontWeight="900"
                  color={NAVY}
                  textTransform="uppercase"
                  letterSpacing="tight"
                  lineHeight="0.85"
                >
                  WIN
                </Text>
                <Text
                  fontSize={{ base: "3xl", md: "5xl" }}
                  fontWeight="900"
                  color={SOLAR_GOLD}
                  textTransform="uppercase"
                  letterSpacing="tight"
                  lineHeight="1"
                >
                  4 BRONCOS
                </Text>
                <Text
                  fontSize={{ base: "3xl", md: "5xl" }}
                  fontWeight="900"
                  color={NAVY}
                  textTransform="uppercase"
                  letterSpacing="tight"
                  lineHeight="1"
                >
                  TICKETS
                </Text>
              </VStack>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.700"
                maxW="2xl"
                lineHeight="1.8"
                pt={2}
              >
                Purchase a solar and battery package this month and go in the draw to WIN 4 premium tickets to an unforgettable Brisbane Broncos game day experience — including food and drinks in our outdoor corporate box on the half way line.
              </Text>
            </VStack>
          </Box>
        </Container>
      </Box>

      {/* ── SECTION 2: PRIZE BREAKDOWN — TILES + BRONCOS IMAGE ── */}
      <Box bg="white" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={3} mb={14} textAlign="center">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="widest"
              textTransform="uppercase"
              color={SOLAR_GOLD}
            >
              Prize Breakdown
            </Text>
            <Heading
              fontSize={{ base: "3xl", md: "5xl" }}
              fontWeight="900"
              color={NAVY}
              textTransform="uppercase"
              letterSpacing="tight"
              lineHeight="1.05"
            >
              Here's What You Could Win
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="gray.700"
              maxW="xl"
              lineHeight="1.8"
            >
              Every solar and battery purchase this month goes in the draw for the ultimate game day experience.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="start">
            {/* Left: prize tiles + entry mechanic */}
                <VStack spacing={5} align="stretch">
                  <SimpleGrid columns={2} spacing={4}>
                    {PRIZE_TILES.map((tile) => (
                      <Box
                        key={tile.label}
                        bg="gray.50"
                        borderRadius="2xl"
                        p={5}
                        borderTop="3px solid"
                        borderColor={tile.accent}
                        transition="transform 0.2s, box-shadow 0.2s"
                        _hover={{ transform: "translateY(-4px)", boxShadow: "lg" }}
                      >
                        <Icon as={tile.icon} w={6} h={6} color={tile.accent} mb={3} />
                        <Text
                          fontWeight="800"
                          fontSize="sm"
                          color={NAVY}
                          textTransform="uppercase"
                          letterSpacing="tight"
                          mb={1}
                          lineHeight="1.2"
                        >
                          {tile.label}
                        </Text>
                        <Text fontSize="xs" color="gray.600" lineHeight="1.7">
                          {tile.detail}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>

                  {/* How to enter */}
                  <Box
                    bg={NAVY}
                    borderRadius="2xl"
                    p={6}
                    borderLeft="4px solid"
                    borderColor={WARM_AMBER}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      letterSpacing="widest"
                      textTransform="uppercase"
                      color={WARM_AMBER}
                      mb={2}
                    >
                      How to Enter
                    </Text>
                    <Text fontWeight="600" fontSize="sm" color="white" lineHeight="1.8">
                      Purchase any solar and battery package during June 2026 and you're automatically entered. One winner drawn at the end of the month.
                    </Text>
                    <Box
                      as="a"
                      href="https://www.onesolaraustralia.com.au/promotion-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      display="inline-block"
                      mt={3}
                      fontSize="xs"
                      color="whiteAlpha.500"
                      textDecoration="underline"
                      _hover={{ color: SOLAR_GOLD }}
                      transition="color 0.2s"
                    >
                      Terms & Conditions apply
                    </Box>
                  </Box>
                </VStack>
              </SimpleGrid>
            </Container>
          </Box>

          {/* ── SECTION 3: ENTER NOW — HALF WAY LINE + FORM ── */}
                <Box id="enter-draw" bg={NAVY} py={{ base: 12, md: 20 }}>
                  <Container maxW="container.xl">
                    <VStack spacing={3} mb={12} textAlign="center">
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        letterSpacing="widest"
                        textTransform="uppercase"
                        color={SOLAR_GOLD}
                      >
                        Enter the Draw
                      </Text>
                      <Heading
                        fontSize={{ base: "3xl", md: "5xl" }}
                        fontWeight="900"
                        color="white"
                        textTransform="uppercase"
                        letterSpacing="tight"
                        lineHeight="1.05"
                      >
                        Lock In Your Entry
                      </Heading>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="whiteAlpha.700"
                        maxW="xl"
                        lineHeight="1.8"
                      >
                        Get your solar and battery quote — and go in the draw while you're at it.
                      </Text>
                    </VStack>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="start">
                      {/* Half way line image + seats callout */}
                      <VStack align="flex-start" spacing={4}>
                        <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl" w="full">
                          <Box
                            as="img"
                            src="/assets/solar-assets/Located-on-the-half-way-line-809x1024.png"
                            alt="Corporate box on the half way line — Brisbane Broncos"
                            w="full"
                            h="auto"
                            display="block"
                          />
                        </Box>
                        <Box
                          bg="rgba(255,255,255,0.06)"
                          borderRadius="xl"
                          px={5}
                          py={4}
                          borderLeft="3px solid"
                          borderColor={SOLAR_GOLD}
                          w="full"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="700"
                            letterSpacing="widest"
                            textTransform="uppercase"
                            color={SOLAR_GOLD}
                            mb={1}
                          >
                            Your Seats
                          </Text>
                          <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7">
                            Outdoor corporate box — located on the half way line at a Brisbane Broncos home game.
                          </Text>
                        </Box>
                      </VStack>

                      {/* Pipedrive form */}
                      <Box
                        className="pipedriveWebForms"
                        data-pd-webforms="https://webforms.pipedrive.com/f/1uKoxbrlBvFInjEstIhaxntIptGdGEjindop9XQaE8AXeiHk90S0lIQ5ENFg9lDBV"
                        minH="400px"
                      />
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* ── SECTION 4: SAME VALUES. DIFFERENT PLAYING FIELD. ── */}
                <Box bg="white" py={{ base: 16, md: 24 }}>
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={14} alignItems="center">
                      {/* Image left on desktop */}
                      <Box
                        borderRadius="2xl"
                        overflow="hidden"
                        boxShadow="xl"
                        order={{ base: 2, md: 1 }}
                      >
                        <Box
                          as="img"
                          src="/assets/solar-assets/FB-Ad-Photo-grid-Facebook-Cover-3.jpg"
                          alt="One Solar Australia team — Brisbane Broncos partnership"
                          w="full"
                          h="auto"
                          display="block"
                        />
                      </Box>

                      {/* Copy right on desktop */}
                      <VStack align="flex-start" spacing={6} order={{ base: 1, md: 2 }}>
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          letterSpacing="widest"
                          textTransform="uppercase"
                          color={SOLAR_GOLD}
                        >
                          Why We Back Local
                        </Text>
                        <Heading
                          fontSize={{ base: "3xl", md: "4xl" }}
                          fontWeight="900"
                          color={NAVY}
                          lineHeight="1.05"
                          textTransform="uppercase"
                          letterSpacing="tight"
                        >
                          Same Values.<br />Different<br />Playing Field.
                        </Heading>
                        <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" lineHeight="1.8">
                          Footy is built on preparation, teamwork, and execution — so is a great solar installation. We bring the same discipline to every Queensland home: premium panels, in-house installers, and local support that doesn't disappear after handover.
                        </Text>
                        <Box
                          bg="gray.50"
                          borderRadius="xl"
                          px={6}
                          py={5}
                          borderLeft="4px solid"
                          borderColor={BURNT_BRONZE}
                          w="full"
                        >
                          <Text color="gray.600" fontSize="sm" lineHeight="1.8" fontStyle="italic">
                            "We're not here for the short game. We're here to build long-term energy independence for Queensland families."
                          </Text>
                        </Box>
                        <Button
                          as={RouterLink}
                          to="/solar"
                          size="md"
                          bg={NAVY}
                          color="white"
                          _hover={{ bg: SOLAR_GOLD }}
                          rightIcon={<Icon as={FaArrowRight} />}
                          px={8}
                          fontWeight="700"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          transition="all 0.2s"
                        >
                          About One Solar
                        </Button>
                      </VStack>
                    </SimpleGrid>
                  </Container>
                </Box>

                {/* ── POWERING QUEENSLAND HOMES / SMARTER ENERGY ── */}
                <Box position="relative" overflow="hidden">
                  <Box
                    as="img"
                    src="/assets/solar-assets/Explore-Solutions-1.jpg"
                    alt="One Solar Australia — Powering Queensland Homes"
                    w="full"
                    display="block"
                    objectFit="cover"
                    maxH={{ base: "320px", md: "485px" }}
                  />
                  <Box
                    position="absolute"
                    inset={0}
                    bg="rgba(255, 255, 255, 0.65)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Container maxW="container.lg">
                      <VStack spacing={5} textAlign="center" color="white">
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          letterSpacing="widest"
                          textTransform="uppercase"
                          color={SOLAR_GOLD}
                        >
                          Powering Queensland Homes
                        </Text>
                        <Heading
                          fontSize={{ base: "3xl", md: "5xl" }}
                          fontWeight="900"
                          textTransform="uppercase"
                          letterSpacing="tight"
                          lineHeight="1.05"
                        >
                          Smarter Energy.<br />Stronger Futures.
                        </Heading>
                        <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" maxW="2xl" lineHeight="1.8">
                          We help Queensland families take control of rising energy costs with premium solar and battery solutions. Designed for Australian conditions. Installed by our in-house team. Backed by local support you can count on.
                        </Text>
                        <Button
                          as={RouterLink}
                          to="/solar/services"
                          size="lg"
                          bg={SOLAR_GOLD}
                          color="white"
                          _hover={{ bg: SOLAR_GOLD_HOVER }}
                          rightIcon={<Icon as={FaArrowRight} />}
                          px={10}
                          fontWeight="700"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          transition="all 0.2s"
                          mt={2}
                        >
                          Explore Our Solutions
                        </Button>
                      </VStack>
                    </Container>
                  </Box>
                </Box>

                {/* ── SECTION 6: FINAL CTA — NAVY BLOCK ── */}
                <Box
                  bg={NAVY}
                  py={{ base: 16, md: 24 }}
                  borderTop="4px solid"
                  borderColor={SOLAR_GOLD}
                >
                  <Container maxW="container.xl">
                    <VStack spacing={6} textAlign="center">
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        letterSpacing="widest"
                        textTransform="uppercase"
                        color={SOLAR_GOLD}
                      >
                        Power Your Home. Power Your Future.
                      </Text>
                      <Heading
                        fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
                        fontWeight="900"
                        color="white"
                        textTransform="uppercase"
                        letterSpacing="tight"
                        lineHeight="0.95"
                      >
                        Make The{" "}
                        <Box as="span" color={SOLAR_GOLD}>
                          Smart
                        </Box>
                        <br />
                        Choice.
                      </Heading>
                      <Text
                        fontSize={{ base: "md", md: "xl" }}
                        color="whiteAlpha.800"
                        maxW="2xl"
                        lineHeight="1.8"
                      >
                        Take control of rising energy costs. Reduce your bills. Support local. And enter to win while you're at it.
                      </Text>
                      <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
                        <Button
                          as={RouterLink}
                          to="/solar/quote"
                          size="lg"
                          bg={SOLAR_GOLD}
                          color="white"
                          _hover={{ bg: SOLAR_GOLD_HOVER }}
                          rightIcon={<Icon as={FaArrowRight} />}
                          px={10}
                          fontWeight="700"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          transition="all 0.2s"
                        >
                          Get Your Free Quote
                        </Button>
                        <Button
                          as={RouterLink}
                          to="/solar"
                          size="lg"
                          variant="outline"
                          borderColor="whiteAlpha.500"
                          color="white"
                          _hover={{ bg: "whiteAlpha.100", borderColor: SOLAR_GOLD }}
                          px={10}
                          fontWeight="700"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          transition="all 0.2s"
                        >
                          Learn More
                        </Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>

                {/* ── BOTTOM BANNER ── */}
                <Box>
                  <Box
                    as={RouterLink}
                    to="/solar/quote"
                    display="block"
                    cursor="pointer"
                    _hover={{ opacity: 0.92 }}
                    transition="opacity 0.2s"
                  >
                    <Box
                      as="img"
                      src="/assets/solar-assets/Website-Banners-1920-700px.jpg"
                      alt="See How Much You Can Save With Solar — One Solar Australia"
                      w="full"
                      display="block"
                    />
                  </Box>
                </Box>

                <SolarFooter />
              </Box>
              );
};

              export default SolarGiveawayJune;
